import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, IconButton } from "@mui/material";
import RewampTabStrip from "./RewampTabStrip";
import RewampComingSoonDialog from "./dialog/RewampComingSoonDialog";
import HomeNavIcon from "./icons/HomeNavIcon";
import HelpSupportNavIcon from "./icons/HelpSupportNavIcon";
import AllServicesNavIcon from "./icons/AllServicesNavIcon";
import { useAiChat } from "../context/AiChatContext";
import { useRewampLocalization, useRewampT } from "../localisation";
import { PWA_BOTTOM_BAR_ITEMS, activeBottomBarItem } from "../layouts/pwaConfig";
import { applyBottomBarHeight } from "../layouts/navMetrics";
import botIcon from "../../assets/rewamp/ai-assistant/bihar-krishi-ai-bot.svg";

const ICONS = { home: HomeNavIcon, help: HelpSupportNavIcon, services: AllServicesNavIcon };

const hrefFor = (path) => `${window.contextPath}/${path}`;

export default function PwaBottomBar() {
  useRewampLocalization(["common"]);
  const t = useRewampT();
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();
  const { isOpen, isMobile, toggleChat } = useAiChat();
  const [comingSoonOpen, setComingSoonOpen] = useState(false);
  const barRef = useRef(null);

  useLayoutEffect(() => {
    const el = barRef.current;
    if (!el) return undefined;
    const publish = () => applyBottomBarHeight(el.offsetHeight);
    const clear = publish();
    if (typeof ResizeObserver === "undefined") return clear;
    const observer = new ResizeObserver(publish);
    observer.observe(el);
    return () => {
      observer.disconnect();
      clear();
    };
  }, []);

  const tabs = useMemo(
    () =>
      PWA_BOTTOM_BAR_ITEMS.map((item) => ({
        id: item.id,
        label: t(item.labelKey),
        icon: ICONS[item.id],
      })),
    [t]
  );

  const activeId = activeBottomBarItem({ pathname, hash });

  const handleChange = (id) => {
    const item = PWA_BOTTOM_BAR_ITEMS.find((i) => i.id === id);
    if (item?.comingSoon) {
      setComingSoonOpen(true);
      return;
    }
    if (item?.path) navigate(hrefFor(item.path));
  };

  const chatButton = (
    <IconButton
      onClick={toggleChat}
      aria-label={t("PWA_BOTTOM_BAR.CHAT")}
      aria-expanded={isMobile ? undefined : isOpen}
      aria-haspopup={isMobile ? undefined : "dialog"}
      sx={{
        width: 60,
        height: 60,
        p: 0,
        flexShrink: 0,
        alignSelf: "center",
        "&:focus-visible": {
          outline: (theme) => `3px solid ${theme.palette.accent?.butterscotch}`,
          outlineOffset: "2px",
        },
      }}
    >
      <Box
        component="img"
        src={botIcon}
        alt=""
        aria-hidden="true"
        sx={{ width: "100%", height: "100%", display: "block" }}
      />
    </IconButton>
  );

  return (
    <Box
      ref={barRef}
      sx={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: (theme) => theme.zIndex.appBar,
        backgroundColor: "neutral.surface",
        borderTop: "1px solid",
        borderTopColor: "neutral.stroke",
        pb: "env(safe-area-inset-bottom, 0px)",
      }}
    >
      <RewampTabStrip
        appearance="bottomNav"
        tabs={tabs}
        activeId={activeId ?? ""}
        onChange={handleChange}
        ariaLabel={t("PWA_BOTTOM_BAR.LABEL")}
        idPrefix="pwa-bottom-bar"
        trailing={chatButton}
      />
      <RewampComingSoonDialog open={comingSoonOpen} onClose={() => setComingSoonOpen(false)} />
    </Box>
  );
}
